/**
 * The wording on the Prospects page that depends on which CRM the book came from.
 *
 * Most of the page reads the same for every CRM. What differs is the CRM's name and what it calls
 * the records behind a reason: Redtail keeps a household, accounts and appointments; Salesforce has
 * relationships between contacts, won opportunities and events. A citation that named the wrong one
 * would send an advisor looking for a screen their CRM does not have.
 *
 * The scoring service names where a value came from with the same three words for every CRM —
 * `household`, `accounts`, `activities` — and `origins` says what each is called here. A CRM with no
 * wording of its own gets plain terms built from its name.
 */
import type { Prospecting } from '@client/api/types';
import { crmName } from '@/util/crmNames';

export interface CrmCopy {
  name: string;
  /** Where a value came from when it was not read from a mapped field. */
  origins: Record<Exclude<Prospecting.ReasonCitation['origin'], 'field'>, string>;
  /** The note shown when a long-term care or hybrid product is on record. */
  ltcProductHeld: string;
  /** Completes "a parent …": how this CRM records one. */
  parentRecorded: string;
}

const COPY: Partial<Record<Prospecting.Provider, Omit<CrmCopy, 'name'>>> = {
  redtail: {
    origins: {
      household: 'Household in Redtail',
      accounts: 'Accounts in Redtail',
      activities: 'Appointments in Redtail',
    },
    ltcProductHeld: 'Has an LTC or hybrid account in Redtail',
    parentRecorded: 'recorded in the household',
  },
  salesforce: {
    origins: {
      household: 'Relationships in Salesforce',
      accounts: 'Won opportunities in Salesforce',
      activities: 'Events in Salesforce',
    },
    ltcProductHeld: 'Has an LTC or hybrid product on a won opportunity in Salesforce',
    parentRecorded: 'linked to the contact',
  },
};

export function crmCopy(provider: Prospecting.Provider): CrmCopy {
  const name = crmName(provider);
  return {
    name,
    ...(COPY[provider] ?? {
      origins: {
        household: `Relationships in ${name}`,
        accounts: `Accounts in ${name}`,
        activities: `Meetings in ${name}`,
      },
      ltcProductHeld: `Has an LTC or hybrid product recorded in ${name}`,
      parentRecorded: 'linked to the contact',
    }),
  };
}
